/**
 * Client-side logging.
 *
 * Concept §5 forbids telemetry that captures keys, fragments, full invitation
 * URLs or plaintext. V1 ships **no** analytics, no session replay and no
 * exception-reporting service — the safest way to keep that promise.
 *
 * What remains is a development console helper that scrubs anything passing
 * through it, so a debugging statement cannot accidentally print a key.
 */

const FRAGMENT = /#[^\s"']*/g;
const QUERY = /\?[^\s"'#]*/g;

export function scrub(value: unknown): unknown {
  if (typeof value === 'string') {
    return value.replace(FRAGMENT, '#[redacted]').replace(QUERY, '?[redacted]');
  }
  if (value instanceof Error) {
    return { name: value.name, message: scrub(value.message) };
  }
  if (Array.isArray(value)) return value.map(scrub);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      const lower = key.toLowerCase();
      out[key] =
        lower.includes('key') ||
        lower.includes('token') ||
        lower.includes('envelope') ||
        lower.includes('name') ||
        lower === 'body' ||
        lower === 'ct'
          ? '[redacted]'
          : scrub(entry);
    }
    return out;
  }
  return value;
}

const enabled = import.meta.env.DEV;

export const log = {
  debug(message: string, fields?: unknown) {
    if (!enabled) return;
    // eslint-disable-next-line no-restricted-globals
    console.debug(`[tc] ${message}`, fields === undefined ? '' : scrub(fields));
  },
  warn(message: string, fields?: unknown) {
    // eslint-disable-next-line no-restricted-globals
    console.warn(`[tc] ${message}`, fields === undefined ? '' : scrub(fields));
  },
  error(message: string, fields?: unknown) {
    // eslint-disable-next-line no-restricted-globals
    console.error(`[tc] ${message}`, fields === undefined ? '' : scrub(fields));
  },
};
